/**
 * Defers a callback to execute when the browser's main thread is idle.
 *
 * Uses `requestIdleCallback` when available, falling back to a 2000ms
 * `setTimeout` for browsers that don't support it (e.g., Safari < 17).
 *
 * This ensures non-critical work (like analytics/tracking initialization)
 * doesn't compete with hydration or user interactions on the critical path.
 *
 * @param callback - The function to execute during idle time
 * @returns A cleanup function that cancels the scheduled callback
 */
export function deferToIdle(callback: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  if ('requestIdleCallback' in window) {
    const id = window.requestIdleCallback(callback);
    return () => window.cancelIdleCallback(id);
  }

  // Fallback: 2000ms setTimeout for browsers without requestIdleCallback
  const timeoutId = setTimeout(callback, 2000);
  return () => clearTimeout(timeoutId);
}
